import React, { useState, useRef } from 'react';
import { ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { isEmpty } from 'lodash';

interface IProps {
  paymentSelected: string;
  isDisabled: boolean;
  /** Booking-in-flight flag. The page-level overlay in ActivityCheckout renders the spinner; kept
   *  here so the parent can still pass it (and for the disabled/overlay guard below). */
  isLoading?: boolean;
  onExpressWalletAvailabilityResolved: (hasWallets: boolean, availableMethods?: any) => void;
  onCompleteBooking: (isCheckoutExpress: boolean, stripe: any, elements: any, paymentDescription?: string) => Promise<any>;
  enabledMethods?: string[];
}

/** Maps the Stripe ExpressCheckoutElement `expressPaymentType` to the wallet payment description that gets
 *  persisted on the booking payment (e.g. "Amazon pay" / "Google pay"). */
function paymentDescriptionFromExpressType(expressPaymentType?: string): string {
  const key = String(expressPaymentType ?? '').toLowerCase().replace(/-/g, '_');
  const map: Record<string, string> = {
    google_pay: 'Google pay',
    apple_pay: 'Apple pay',
    amazon_pay: 'Amazon pay',
    paypal: 'Paypal',
    link: 'Link',
  };
  return map[key] ?? (expressPaymentType ? String(expressPaymentType).replace(/_/g, ' ') : 'Express checkout');
}

export const ActivityExpressCheckoutButtons: React.FC<IProps> = ({
  paymentSelected,
  isDisabled,
  onExpressWalletAvailabilityResolved,
  onCompleteBooking,
  enabledMethods = [],
}) => {
  const [visibility, setVisibility] = useState('hidden');
  const stripe = useStripe();
  const elements = useElements();

  const lastAvailabilityReportRef = useRef<boolean | null>(null);

  const onReady = (event: any) => {
    const available = event?.availablePaymentMethods;
    if (available === undefined || available === null) {
      return;
    }
    const hasWallets = !!(
      available?.applePay ||
      available?.googlePay ||
      available?.link ||
      available?.paypal ||
      available?.amazonPay
    );

    if (lastAvailabilityReportRef.current !== hasWallets) {
      lastAvailabilityReportRef.current = hasWallets;
    }
    onExpressWalletAvailabilityResolved?.(hasWallets, available);
    
    if (hasWallets) {
      setVisibility('block');
    }
  };

  const onConfirm = async (event: any) => {
    if (!stripe || !elements) {
      return;
    }
    const { error: submitError } = await elements.submit();
    if (submitError) {
      return;
    }
    try {
      const success = await onCompleteBooking(true, stripe, elements, paymentDescriptionFromExpressType(event?.expressPaymentType));
      if (!success) {
        event.paymentFailed({ reason: 'fail' });
      }
    } catch (err: any) {
      event.paymentFailed({ reason: 'fail' });
    }
  };


  function getPaymentMethods(
    enabledMethods: string[],
    paymentSelected: string
  ): any {
    if (isEmpty(enabledMethods) && isEmpty(paymentSelected)) {
      return {};
    }

    if (isEmpty(enabledMethods)) {
      return {
        amazonPay: 'never',
        applePay: 'never',
        googlePay: 'never',
        link: 'never',
        paypal: 'never',
        klarna: 'never',
      };
    }

    return {
      amazonPay: enabledMethods.includes('amazon_pay')
        ? paymentSelected === 'amazon_pay' || paymentSelected === ''
          ? 'auto'
          : 'never'
        : 'never',
      applePay: enabledMethods.includes('apple_pay')
        ? paymentSelected === 'apple_pay' || paymentSelected === ''
          ? 'always'
          : 'never'
        : 'never',
      googlePay: enabledMethods.includes('google_pay')
        ? paymentSelected === 'google_pay' || paymentSelected === ''
          ? 'always'
          : 'never'
        : 'never',
      link: enabledMethods.includes('link')
        ? paymentSelected === 'link' || paymentSelected === ''
          ? 'auto'
          : 'never'
        : 'never',
      paypal: enabledMethods.includes('paypal')
        ? paymentSelected === 'paypal' || paymentSelected === ''
          ? 'auto'
          : 'never'
        : 'never',
      // Always 'never'. Klarna IS an ExpressCheckoutElement method, so omitting this key (which
      // defaults to 'auto') let Stripe render a stray Klarna button here whenever the Stripe account
      // had Klarna on. The Klarna row is a force-full feature (see the force-full picker); this
      // express strip stays the wallets it has always been.
      klarna: 'never',
    };
  }

  const paymentMethodsConfig = getPaymentMethods(enabledMethods, paymentSelected);
  const elementKey = JSON.stringify(paymentMethodsConfig);

  return (
    <>
      <div style={{ position: 'relative' }}>
      <div
        style={{
          display: visibility === 'hidden' ? 'none' : 'block',
          opacity: isDisabled ? 0.5 : 1,
          cursor: isDisabled ? 'not-allowed' : 'pointer',
        }}
      >
        <ExpressCheckoutElement
          key={elementKey}
          onConfirm={onConfirm}
          onReady={onReady}
          options={{
            paymentMethods: paymentMethodsConfig,
            paymentMethodOrder: ['applePay', 'googlePay', 'link', 'paypal', 'amazonPay'],
          }}
        />
      </div>
      {isDisabled && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'transparent',
            cursor: 'not-allowed',
            zIndex: 10,
          }}
        />
      )}
      </div>
    </>
  );
};
